// 봉투의 칸 이름(kind)을 도구가 알아듣는 표준 속성명(fw.error.kind, http.route …)으로 옮긴다.
// 조회·알림에 쓰는 이름을 SDK 자동 수집에 기대지 않고 직접 심어야 도구를 바꿔도 검색이 깨지지 않는다.

import type {
  AppError,
  ErrorFlow,
  ErrorKind,
  ErrorMechanism,
  ErrorOrigin,
  ErrorSeverity,
} from './AppError'

export type ReportLevel = 'fatal' | 'error' | 'warning'
// 태그 모드(dropMode: 'tag')에서만 붙는다. drop 은 "평소라면 버렸을 것" — 버리는 규칙이 맞는지 도구 안에서 검증하는 용도.
export type ReportVerdict = 'drop' | 'send'

export type ReportTags = {
  'fw.error.kind': ErrorKind
  'fw.error.severity': ErrorSeverity
  'fw.error.origin': ErrorOrigin
  'fw.error.code'?: string
  'fw.flow'?: ErrorFlow
  'fw.mechanism'?: ErrorMechanism
  'fw.verdict'?: ReportVerdict
  'http.request.method'?: string
  'http.response.status_code'?: string
  'http.route'?: string
}

export type ReportEvent = {
  error: AppError
  // 어댑터가 captureException 에 넘길 것. 원본이 있으면 원본 — 도구가 원본 스택으로 그룹핑한다.
  original: unknown
  level: ReportLevel
  fingerprint?: string[]
  tags: ReportTags
  contexts: { 'url.path'?: string }
}

// url.path 는 외부 SaaS 로 나간다. 키를 단어 조각(accessToken → access·token, x-auth-token → x·auth·token)으로
// 쪼개 조각 단위로 비교해야 authorId 는 남기고 OAuth code·서명(X-Amz-Signature)·API key 는 지울 수 있다.
const SENSITIVE_SEGMENTS: readonly string[] = [
  'token',
  'auth',
  'authorization',
  'password',
  'passwd',
  'pwd',
  'secret',
  'session',
  'code',
  'key',
  'apikey',
  'signature',
]

// 대문자 앞마다 자르면 TOKEN · API_KEY 가 한 글자씩 쪼개져 놓친다. 소문자→대문자(accessToken)와
// 약어 끝(APIToken → API·Token) 경계에서만 자른다. 구형 iOS 웹뷰가 lookbehind 를 못 읽어 replace 로 경계를 표시한다.
const segmentsOf = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .split(/[-_.\s]/)
    .filter(Boolean)
    .map((segment) => segment.toLowerCase())

export const isSensitiveParam = (key: string) =>
  segmentsOf(key).some((segment) => SENSITIVE_SEGMENTS.includes(segment))

export const stripAuthParams = (urlPath: string): string => {
  const at = urlPath.indexOf('?')
  if (at < 0) return urlPath
  const path = urlPath.slice(0, at)
  const query = urlPath.slice(at + 1)
  const params = new URLSearchParams(query)
  for (const key of [...params.keys()]) {
    if (isSensitiveParam(key)) params.delete(key)
  }
  const rest = params.toString()
  return rest ? `${path}?${rest}` : path
}

const compact = <T extends Record<string, unknown>>(object: T): T =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined)) as T

export const toReportEvent = (
  error: AppError,
  fingerprint?: string[],
  verdict?: ReportVerdict,
): ReportEvent => ({
  error,
  original: error.cause ?? error,
  level: error.severity,
  fingerprint,
  tags: compact({
    'fw.error.kind': error.kind,
    'fw.error.severity': error.severity,
    'fw.error.origin': error.origin,
    'fw.error.code': error.code,
    'fw.flow': error.flow,
    'fw.mechanism': error.mechanism,
    'fw.verdict': verdict,
    'http.request.method': error.method,
    'http.response.status_code': error.status === undefined ? undefined : String(error.status),
    'http.route': error.route,
  }),
  contexts: compact({
    'url.path': error.urlPath === undefined ? undefined : stripAuthParams(error.urlPath),
  }),
})
